import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Pencil } from 'lucide-react';
import { useMarket } from '../hooks/useMarket';

const STORAGE_KEY = 'lh_user_budget';

export const readBudget = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    const min = Number(obj.min) || 0;
    const max = Number(obj.max) || 0;
    if (max <= 0) return null;
    return { min, max };
  } catch {
    return null;
  }
};

const writeBudget = (b) => {
  try {
    if (!b) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
  } catch {
    /* noop */
  }
  window.dispatchEvent(new CustomEvent('lh:budget-changed'));
};

const makeFmt = (symbol) => (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${symbol}${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 2).replace(/\.?0+$/, '')}m`;
  if (v >= 1_000) return `${symbol}${Math.round(v / 1_000)}k`;
  return `${symbol}${v}`;
};

/**
 * Inline budget pill shown above any rail driven by `/for-your-taste`.
 *
 * Three visual states:
 *   • derived (passive label, source from API):
 *     "In your range — €332k to €618k · adjust"
 *   • user-set (purple chip): "Your budget — €350k to €600k · edit"
 *   • absent: pill is hidden (parent renders no label).
 *
 * Click → popover with min / max € inputs. Save fires `lh:budget-changed`
 * so every other mounted rail re-fetches instantly.
 */
const BudgetPill = ({ budgetWindow }) => {
  const market = useMarket();
  const fmt = makeFmt(market.currency_symbol || '€');
  const currencyWord = market.isUK ? 'sterling' : market.isUS ? 'dollars' : 'euro';
  const kLabel = `${market.currency_symbol || '€'}k`;
  const [open, setOpen] = useState(false);
  const [userBudget, setUserBudget] = useState(() => readBudget());
  const [draftMin, setDraftMin] = useState('');
  const [draftMax, setDraftMax] = useState('');

  // Stay in sync if another component clears/sets the budget.
  useEffect(() => {
    const sync = () => setUserBudget(readBudget());
    window.addEventListener('lh:budget-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('lh:budget-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Decide what to show. User-set always wins.
  const isUser = !!userBudget;
  const min = isUser ? userBudget.min : (budgetWindow ? budgetWindow.min : 0);
  const max = isUser ? userBudget.max : (budgetWindow ? budgetWindow.max : 0);
  const hasWindow = max > 0 && !(budgetWindow && String(budgetWindow.source || '').endsWith('_dropped'));
  if (!isUser && !hasWindow) return null;

  // Seed the form with the active window when popover opens.
  const onOpenChange = (o) => {
    setOpen(o);
    if (o) {
      setDraftMin(min ? String(Math.round(min / 1000)) : '');
      setDraftMax(max ? String(Math.round(max / 1000)) : '');
    }
  };

  const apply = () => {
    const mn = Math.max(0, (Number(draftMin) || 0) * 1000);
    const mx = Math.max(0, (Number(draftMax) || 0) * 1000);
    if (mx <= 0 || mx < mn) return;
    writeBudget({ min: mn, max: mx });
    setUserBudget({ min: mn, max: mx });
    setOpen(false);
  };

  const clearUser = () => {
    writeBudget(null);
    setUserBudget(null);
    setOpen(false);
  };

  const dotColor = isUser ? 'bg-[#7042cc]' : 'bg-emerald-500';
  const label = isUser
    ? `Your budget — ${fmt(min)} to ${fmt(max)}`
    : `In your range — ${fmt(min)} to ${fmt(max)}`;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="budget-pill"
          data-user-set={isUser ? 'true' : 'false'}
          className={`mt-1.5 inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 border transition-colors ${
            isUser
              ? 'border-[#7042cc]/30 bg-[#F5EFFF] text-[#5b35a8] hover:bg-[#ede3ff]'
              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotColor}`} />
          <span>{label}</span>
          <span className="text-gray-400 px-0.5">·</span>
          <span className="inline-flex items-center gap-1 font-medium">
            <Pencil className="h-3 w-3" />
            {isUser ? 'edit' : 'adjust'}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80">
        <div className="space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Set your budget</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              We&rsquo;ll only show you homes in this range across the site. Values in {currencyWord} thousands.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1 block">
                Min ({kLabel})
              </label>
              <Input
                type="number"
                inputMode="numeric"
                placeholder="e.g. 350"
                value={draftMin}
                onChange={(e) => setDraftMin(e.target.value)}
                data-testid="budget-min-input"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold mb-1 block">
                Max ({kLabel})
              </label>
              <Input
                type="number"
                inputMode="numeric"
                placeholder="e.g. 600"
                value={draftMax}
                onChange={(e) => setDraftMax(e.target.value)}
                data-testid="budget-max-input"
              />
            </div>
          </div>
          <div className="flex items-center justify-between pt-1">
            {isUser ? (
              <button
                type="button"
                onClick={clearUser}
                data-testid="budget-clear"
                className="text-xs text-gray-500 hover:text-gray-700 underline"
              >
                Clear my budget
              </button>
            ) : (
              <span className="text-[11px] text-gray-400">Saved across the site</span>
            )}
            <Button
              size="sm"
              onClick={apply}
              data-testid="budget-apply"
              className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default BudgetPill;
